import * as React from 'react';
import { Button, type ButtonProps } from './button';
import { cn } from '@/lib/cn';

export interface LoadingButtonProps extends ButtonProps {
    /** Si true, muestra spinner y deshabilita interacción. */
    isLoading?: boolean;
    /** Texto opcional que reemplaza al children mientras isLoading=true. */
    loadingText?: string;
}

/**
 * Spinner SVG inline — no requiere dep extra de iconos.
 */
function Spinner({ className }: { className?: string }) {
    return (
        <svg
            className={cn('animate-spin', className)}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
        >
            <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
            />
            <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
        </svg>
    );
}

/**
 * Botón con estado de carga.
 *
 * - `isLoading=true` → muestra spinner + deshabilita interacción + muestra `loadingText` si fue pasado.
 * - Si `loadingText` no se pasa, mantiene el `children` original con el spinner al lado.
 * - Hereda todas las variants/sizes del Button base (default, destructive, outline, secondary, ghost, link).
 */
export const LoadingButton = React.forwardRef<HTMLButtonElement, LoadingButtonProps>(
    ({ isLoading = false, loadingText, disabled, children, ...buttonProps }, ref) => {
        return (
            <Button
                ref={ref}
                disabled={isLoading || disabled}
                aria-busy={isLoading}
                {...buttonProps}
            >
                {isLoading && <Spinner className="h-4 w-4" />}
                {isLoading && loadingText ? loadingText : children}
            </Button>
        );
    },
);
LoadingButton.displayName = 'LoadingButton';
